import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { EmployeeLayout } from '../components/EmployeeLayout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Inbox, Loader2, Check, X, Clock, AlertTriangle, RefreshCw, MessageSquare,
} from 'lucide-react';
import { shiftReplacementAPI } from '../lib/api';

const REASON_LABELS = {
  sick: 'Sick',
  family_emergency: 'Family emergency',
  appointment: 'Appointment',
  transportation_issue: 'Transportation issue',
  personal: 'Personal',
  other: 'Other',
};

const URGENCY = {
  today: { cls: 'bg-rose-100 text-rose-800 border-rose-200 ring-rose-300', label: 'TODAY' },
  tomorrow: { cls: 'bg-amber-100 text-amber-800 border-amber-200 ring-amber-300', label: 'TOMORROW' },
  future: { cls: 'bg-slate-100 text-slate-600 border-slate-200', label: 'FUTURE' },
};

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  accepted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  declined: 'bg-rose-100 text-rose-700 border-rose-200',
  expired: 'bg-slate-100 text-slate-500 border-slate-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

export default function ShiftReplacements() {
  const { user } = useAuth();
  const LayoutComponent = user?.role === 'staff' ? EmployeeLayout : Layout;
  const isManager = user?.role && user.role !== 'staff';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);
  const [declineFor, setDeclineFor] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  // Employee-initiated coverage requests (manager review queue)
  const [coverage, setCoverage] = useState([]);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [reviewModal, setReviewModal] = useState({ open: false, mode: null, req: null });
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await shiftReplacementAPI.inbox();
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadCoverage = useCallback(async () => {
    if (!isManager) return;
    setCoverageLoading(true);
    try {
      const r = await shiftReplacementAPI.employeeRequestsList('tenant');
      setCoverage(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // Silent — not critical to main inbox flow
    } finally {
      setCoverageLoading(false);
    }
  }, [isManager]);

  useEffect(() => { loadCoverage(); }, [loadCoverage]);

  const pendingCoverage = useMemo(
    () => coverage.filter(c => c.status === 'pending_manager_approval')
                  .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
    [coverage],
  );

  const handleCoverageReview = async () => {
    if (!reviewModal.req) return;
    setReviewBusy(true);
    try {
      const id = reviewModal.req.id;
      const payload = reviewNotes.trim() ? { manager_notes: reviewNotes.trim() } : {};
      if (reviewModal.mode === 'approve') {
        await shiftReplacementAPI.employeeRequestApprove(id, payload);
        toast.success('Coverage request approved');
      } else {
        await shiftReplacementAPI.employeeRequestReject(id, payload);
        toast.success('Coverage request rejected');
      }
      setReviewModal({ open: false, mode: null, req: null });
      setReviewNotes('');
      await loadCoverage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setReviewBusy(false);
    }
  };

  const handleAccept = async (id) => {
    setActing(id + ':accept');
    try {
      await shiftReplacementAPI.accept(id);
      toast.success('Shift accepted — you are now scheduled');
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || 'Accept failed';
      if (status === 409) {
        toast.error(detail);
        load(); // refresh — most likely race/expiry
      } else {
        toast.error(detail);
      }
    } finally {
      setActing(null);
    }
  };

  const handleDecline = async () => {
    if (!declineFor) return;
    setActing(declineFor + ':decline');
    try {
      await shiftReplacementAPI.decline(declineFor, declineReason);
      toast.success('Declined');
      setDeclineFor(null);
      setDeclineReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decline failed');
    } finally {
      setActing(null);
    }
  };

  const pendingCount = items.filter((i) => i.status === 'pending').length;

  return (
    <LayoutComponent>
      <div className="space-y-4" data-testid="replacements-inbox-page">
        {isManager && (
          <div
            className="rounded-lg border border-orange-200 bg-orange-50/50 p-4"
            data-testid="coverage-requests-card"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                Coverage Requests
                {pendingCoverage.length > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-orange-500 text-white text-[11px] font-semibold"
                    data-testid="coverage-pending-count"
                  >
                    {pendingCoverage.length}
                  </span>
                )}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={loadCoverage}
                disabled={coverageLoading}
                data-testid="coverage-refresh"
              >
                {coverageLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Staff-initiated coverage requests awaiting your approval.
            </p>
            {pendingCoverage.length === 0 ? (
              <div className="mt-3 text-sm text-slate-500" data-testid="coverage-empty">
                No coverage requests pending approval.
              </div>
            ) : (
              <ol className="mt-3 space-y-2">
                {pendingCoverage.map((c) => {
                  const s = c.shift_snapshot || {};
                  return (
                    <li
                      key={c.id}
                      className="rounded-lg border bg-white p-3"
                      data-testid={`coverage-row-${c.id}`}
                    >
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-900">
                              {c.original_user_name || c.original_user_email || 'Staff'}
                            </span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-orange-100 text-orange-700 border-orange-200">
                              {REASON_LABELS[c.reason] || c.reason}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5 font-mono">
                            {s.date} · {s.start_time}–{s.end_time}
                            {s.role ? ` · ${s.role}` : ''}
                          </p>
                          {c.details && (
                            <div className="mt-2 text-sm text-slate-700 rounded bg-slate-50 border p-2 whitespace-pre-wrap">
                              {c.details}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                            onClick={() => { setReviewModal({ open: true, mode: 'approve', req: c }); setReviewNotes(''); }}
                            data-testid={`coverage-approve-${c.id}`}
                          >
                            <Check className="h-3.5 w-3.5 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-rose-200 text-rose-700 hover:bg-rose-50 h-8 text-xs"
                            onClick={() => { setReviewModal({ open: true, mode: 'reject', req: c }); setReviewNotes(''); }}
                            data-testid={`coverage-reject-${c.id}`}
                          >
                            <X className="h-3.5 w-3.5 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Inbox className="h-6 w-6 text-indigo-600" />
              Replacement Inbox
              {pendingCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-rose-500 text-white text-[11px] font-semibold" data-testid="inbox-pending-count">
                  {pendingCount}
                </span>
              )}
            </h1>
            <p className="text-sm text-slate-500 mt-1">Shifts your manager has asked you to cover.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="inbox-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400">
            <Loader2 className="inline h-4 w-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border bg-white p-10 text-center text-slate-400" data-testid="inbox-empty">
            Nothing in your inbox. When your manager needs a cover, you'll see it here.
          </div>
        ) : (
          <ol className="space-y-2">
            {items.map((it) => {
              const u = URGENCY[it.urgency || 'future'];
              const s = it.shift_snapshot || {};
              return (
                <li
                  key={it.id}
                  className={`rounded-lg border bg-white p-3 ${it.status === 'pending' && (it.urgency === 'today' || it.urgency === 'tomorrow') ? `ring-1 ${u.cls.split(' ').find((c) => c.startsWith('ring-')) || ''}` : ''}`}
                  data-testid={`inbox-item-${it.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm font-mono">
                          {s.date} · {s.start_time}–{s.end_time}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${u.cls.replace('ring-', '').split(' ').filter((c) => !c.startsWith('ring-')).join(' ')}`}>{u.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_BADGE[it.status] || ''}`}>{it.status}</span>
                        {s.role && <span className="text-xs text-slate-500">{s.role}</span>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Requested by {it.manager_email || 'manager'} · {new Date(it.created_at).toLocaleString()}
                      </div>
                      {it.note && (
                        <div className="text-sm mt-2 rounded bg-slate-50 border p-2 text-slate-700">
                          {it.note}
                        </div>
                      )}
                      {it.expires_at && it.status === 'pending' && (
                        <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" /> Expires at shift start: {new Date(it.expires_at).toLocaleString()}
                        </div>
                      )}
                      {it.status === 'declined' && it.declined_reason && (
                        <div className="text-xs text-rose-700 mt-1">Declined reason: {it.declined_reason}</div>
                      )}
                      {it.status === 'expired' && it.expired_reason && (
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> {it.expired_reason.replace(/_/g, ' ')}
                        </div>
                      )}
                    </div>
                    {it.status === 'pending' && (
                      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handleAccept(it.id)}
                          disabled={acting === it.id + ':accept'}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`accept-${it.id}`}
                        >
                          {acting === it.id + ':accept' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDeclineFor(it.id)}
                          data-testid={`decline-${it.id}`}
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> Decline
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {/* Decline reason inline panel */}
        {declineFor && (
          <div className="fixed bottom-4 right-4 bg-white border rounded-lg shadow-lg p-3 max-w-sm" data-testid="decline-panel">
            <div className="text-sm font-medium mb-1">Decline replacement</div>
            <Input
              autoFocus
              placeholder="Optional reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              data-testid="decline-reason-input"
            />
            <div className="flex justify-end gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => { setDeclineFor(null); setDeclineReason(''); }}>Cancel</Button>
              <Button size="sm" onClick={handleDecline} disabled={acting?.endsWith(':decline')} data-testid="decline-confirm">Confirm decline</Button>
            </div>
          </div>
        )}

        {/* Coverage request review panel (manager approves/rejects employee-initiated) */}
        {reviewModal.open && reviewModal.req && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3"
            data-testid="coverage-review-modal"
          >
            <div className="bg-white rounded-lg shadow-xl p-4 max-w-md w-full">
              <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <MessageSquare size={16} className="text-slate-500" />
                {reviewModal.mode === 'approve' ? 'Approve' : 'Reject'} Coverage Request
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                {reviewModal.req.original_user_name || reviewModal.req.original_user_email} · {reviewModal.req.shift_snapshot?.date} {reviewModal.req.shift_snapshot?.start_time}–{reviewModal.req.shift_snapshot?.end_time}
              </p>
              <div className="mt-3 rounded bg-slate-50 border p-2 text-xs">
                <span className="font-medium text-slate-700">{REASON_LABELS[reviewModal.req.reason] || reviewModal.req.reason}</span>
                {reviewModal.req.details && (
                  <p className="mt-1 text-slate-700 whitespace-pre-wrap">{reviewModal.req.details}</p>
                )}
              </div>
              <div className="mt-3 space-y-1.5">
                <label className="text-xs font-medium text-slate-700">
                  Manager notes <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <Textarea
                  rows={3}
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder={reviewModal.mode === 'approve' ? 'e.g. Take care' : 'e.g. Cannot find coverage on short notice'}
                  maxLength={2000}
                  data-testid="coverage-review-notes"
                />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setReviewModal({ open: false, mode: null, req: null })}
                  disabled={reviewBusy}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCoverageReview}
                  disabled={reviewBusy}
                  className={`text-white ${reviewModal.mode === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-rose-600 hover:bg-rose-700'}`}
                  data-testid="coverage-review-confirm"
                >
                  {reviewBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                  {reviewModal.mode === 'approve' ? 'Approve' : 'Reject'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutComponent>
  );
}
